/**
 * "What happened to vacancies over a period" — the aggregate behind the
 * analytics "Vacancies" tab.
 *
 * Flow numbers (`Kpi`, and the candidate columns of `RecruiterRow`) describe
 * the requested period. `inWork` and `idle` are a snapshot as of now: they do
 * not depend on the period.
 */

/** The "Hire sources" tab, asked with the same `Query`. */
export * as Sources from './vacancy-sources.interface';

/**
 * The candidate status history is stored by UTC day, so the stages are
 * counted over the UTC days of `dateFrom` and `dateTo`.
 */
export interface Query {
  readonly dateFrom: string;
  readonly dateTo: string;
  /** Legal entity codes (EDRPOU) of the vacancies' organization. */
  readonly organization?: string[];
}

export interface Period {
  readonly dateFrom: string;
  readonly dateTo: string;
  /** The window of the same length right before the requested one. */
  readonly prevDateFrom: string;
  readonly prevDateTo: string;
}

export interface Kpi {
  readonly newVacancies: number;
  readonly candidatesAdded: number;
  /**
   * The part of `candidatesAdded` a person added: shortlist entries whose author
   * is not the environment's system user (auto-AI, staff reserve, imported applies).
   */
  readonly candidatesAddedManually: number;
  /** Candidates that entered the interview stage for the first time. */
  readonly interviews: number;
  /** Candidates that entered the hired stage for the first time. */
  readonly hired: number;
  readonly closedVacancies: number;
  /**
   * Average working days of the vacancies closed successfully in the period;
   * `null` when none was closed or none of them counts towards the average.
   */
  readonly avgWorkingDays: number | null;
}

/** Every number is credited to the vacancy's current assignee. */
export interface RecruiterRow {
  readonly userId: number;
  readonly fullName: string;
  readonly image: string | null;
  readonly inWork: number;
  readonly candidatesAdded: number;
  /** See `Kpi.candidatesAddedManually`. */
  readonly candidatesAddedManually: number;
  readonly shortlisted: number;
  readonly interviews: number;
  readonly hired: number;
  /** Vacancies in work with no activity for `idleThresholdDays` or longer. */
  readonly idle: number;
}

export interface IdleVacancy {
  readonly id: number;
  readonly title: string;
  readonly userId: number;
  readonly fullName: string;
  readonly idleDays: number;
  readonly lastActivityAt: string;
}

export interface Response {
  readonly period: Period;
  readonly kpi: Kpi;
  readonly prevKpi: Kpi;
  readonly recruiters: RecruiterRow[];
  /** Every idle vacancy, longest idle first. */
  readonly idleVacancies: IdleVacancy[];
  readonly idleTotal: number;
  readonly idleThresholdDays: number;
}
